import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import { DateTime } from "luxon";

function SEO({
  description,
  lang,
  meta,
  title,
  imageUrl,
  dateISO,
  authors = [],
  article,
  articles,
  currentURL,
  categoryName,
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `,
  );

  const metaDescription = description || site.siteMetadata.description;

  useEffect(() => {
    window.localStorage.setItem("lang", lang);
  }, [lang]);

  const articlesInfo = articles && [
    {
      "@type": "CollectionPage",
      "@id": `https://techblog.smc.it/category/${categoryName}/#webpage`,
      url: currentURL,
      name: site.siteMetadata.title,
      isPartOf: { "@id": "https://techblog.smc.it/#website" },
      inLanguage: lang,
    },
    {
      "@type": "ItemList",
      "@id": `https://techblog.smc.it/category/${categoryName}/#articles`,
      itemListElement: [
        articles.map((article, i) => ({
          "@type": "ListItem",
          url: "https://techblog.smc.it" + article.fields.postPath,
          position: i,
        })),
      ],
    },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@id": `https://techblog.smc.it/#/schema/company`,
        "@type": "Organization",
        name: "SMC Treviso",
        logo: {
          "@type": "ImageObject",
          url:
            "https://www.smc.it/image/layout_set_logo?img_id=945552&t=1580457821176",
          caption: "Logo SMC",
        },
        url: "https://www.smc.it",
      },
      ...authors.map((author) => ({
        "@type": ["Person", "Organization"],
        "@id": `https://techblog.smc.it/#/schema/person/${author.id}`,
        name: author.fullName,
        image: {
          "@type": "ImageObject",
          url:
            site.siteMetadata.siteUrl + author.image.childImageSharp.fixed.src,
          caption: author.fullName,
        },
        logo: {
          "@type": "ImageObject",
          url:
            site.siteMetadata.siteUrl + author.image.childImageSharp.fixed.src,
          caption: author.fullName,
        },
        url: site.siteMetadata.siteUrl,
      })),
      {
        "@type": "WebSite",
        "@id": "https://techblog.smc.it/#/schema/site",
        url: site.siteMetadata.siteUrl,
        name: site.siteMetadata.title,
        inLanguage: lang,
      },
      imageUrl && {
        "@type": "ImageObject",
        "@id": `https://techblog.smc.it/#/schema/article/${article.id}/image`,
        url: site.siteMetadata.siteUrl + imageUrl,
        caption: title,
      },
      article && {
        "@type": "Article",
        author: {
          "@id": `https://techblog.smc.it/#/schema/person/${authors[0].id}`,
        },
        mainEntityOfPage: {
          "@id": "https://techblog.smc.it/#/schema/site",
        },
        headline: title,
        datePublished: dateISO,
        dateModified: dateISO,
        publisher: {
          "@id": `https://techblog.smc.it/#/schema/person/${authors[0].id}`,
        },
        keywords: article.frontmatter.tags.join(","),
        inLanguage: article.fields.langKey,
        image: {
          "@id": `https://techblog.smc.it/#/schema/article/${article.id}/image`,
        },
      },
      ...(articlesInfo || []),
    ].filter(Boolean),
  };

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `article`,
        },
        {
          property: `og:site_name`,
          content: site.siteMetadata.title,
        },
        dateISO && {
          property: `article:published_time`,
          content: dateISO,
        },
        dateISO && {
          property: `article:modified_time`,
          content: dateISO,
        },
        imageUrl && {
          property: `og:image`,
          content: site.siteMetadata.siteUrl + imageUrl,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
      ]
        .filter(Boolean)
        .concat(meta)}
    >
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

SEO.defaultProps = {
  meta: [],
  description: ``,
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
};

export default SEO;
